import {
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import { useAdminInvitations } from "../Hooks/useAdminInvitations";

const AdminInvitationsPage = () => {
  const {
    admins,
    loading,
    error,
    deleteAdmin,
    searchProfile,
    searchLoading,
    searchError,
    searchedProfile,
    setSearchedProfile,
    setInvitationCount,
    setCountLoading,
    setCountError,
    createAdmin,
    createAdminLoading,
    createAdminError,
  } = useAdminInvitations();

  const [searchEmail, setSearchEmail] = useState("");
  const [countInput, setCountInput] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    await searchProfile(searchEmail.trim());
    setCountInput("");
  };

  const handleSetInvitations = async () => {
    if (!searchedProfile || !countInput.trim()) return;
    const count = parseInt(countInput, 10);
    if (isNaN(count) || count < 0) return;
    await setInvitationCount(searchedProfile.inviter_email, count);
  };

  const handleMakeAdmin = async () => {
    if (!searchedProfile) return;
    await createAdmin(searchedProfile.inviter_email);
  };

  const handleDeleteClick = (email: string) => {
    setAdminToDelete(email);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (adminToDelete) {
      const success = await deleteAdmin(adminToDelete);
      if (success) {
        setDeleteDialogOpen(false);
        setAdminToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAdminToDelete(null);
  };

  const isSearchedProfileAdmin = searchedProfile?.permissions === "admin";

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Admin Invitations
      </Typography>

      {/* Profile Search Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Profile Search
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              placeholder="Enter email to search"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!searchEmail.trim() || searchLoading}
            >
              {searchLoading ? <CircularProgress size={20} /> : "Search"}
            </Button>
          </Box>

          {searchError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {searchError}
            </Alert>
          )}

          {searchedProfile && (
            <Box
              sx={{ mt: 3, p: 2, bgcolor: "background.paper", borderRadius: 1 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Profile Found
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Email:</strong> {searchedProfile.inviter_email}
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {searchedProfile.inviter_name || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Invitations:</strong>{" "}
                  {searchedProfile.invitesAvailable}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong>{" "}
                  {isSearchedProfileAdmin ? "Admin" : "Regular User"}
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="Set Invitation Count"
                  type="number"
                  value={countInput}
                  onChange={(e) => setCountInput(e.target.value)}
                  size="small"
                  sx={{ width: 200 }}
                  placeholder="Enter number"
                />
                <Button
                  variant="contained"
                  onClick={handleSetInvitations}
                  disabled={!countInput.trim() || setCountLoading}
                >
                  {setCountLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Set Count"
                  )}
                </Button>
                {!isSearchedProfileAdmin && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleMakeAdmin}
                    disabled={createAdminLoading}
                  >
                    {createAdminLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Make Admin"
                    )}
                  </Button>
                )}
              </Box>

              {setCountError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {setCountError}
                </Alert>
              )}

              {createAdminError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {createAdminError}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Admin List Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h2">
          Admin Users
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && admins.length > 0 && (
        <List>
          {admins.map((admin, index) => (
            <Box key={admin.inviter_email}>
              <ListItem sx={{ py: 2 }}>
                <ListItemText
                  primary={admin.inviter_email}
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {admin.inviter_name || "No name"}
                      </Typography>
                      <br />
                      <Typography component="span" variant="body2">
                        Invitations: {admin.invitesAvailable.toLocaleString()}
                      </Typography>
                    </>
                  }
                  sx={{ flex: 1 }}
                />
                <Button
                  onClick={() => handleDeleteClick(admin.inviter_email)}
                  color="error"
                  variant="outlined"
                  size="small"
                >
                  Remove Admin
                </Button>
              </ListItem>
              {index < admins.length - 1 && <Divider />}
            </Box>
          ))}
        </List>
      )}

      {!loading && !error && admins.length === 0 && (
        <Typography variant="body1" sx={{ mt: 2 }}>
          No admin users found.
        </Typography>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Remove Admin</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to remove admin permissions from "
            {adminToDelete}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminInvitationsPage;
